
import React, { useState, useMemo, useEffect } from "react";
import {
    Table,
    Button,
    Input,
    Select,
    Card,
    Space,
    Typography,
    Tooltip,
    Avatar,
    Row,
    Col,
    Modal,
    Radio,
    message,
    InputNumber
} from "antd";
import {
    FileTextOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    DollarOutlined,
    SearchOutlined,
    ReloadOutlined,
    EditOutlined,
    BankOutlined,
    PrinterOutlined,
    SyncOutlined,
    ExclamationCircleOutlined,
    SwapOutlined,
    CalculatorOutlined,
    MinusCircleOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import { createProvision, downloadFactureTransit, getAllCategoriesTransit, getAllTransitDocument, getTransitFacture, updateStatutFacture } from "../../api/provision";
import { select } from "framer-motion/m";
import Notification from "../../composants/Notification";

const { Text, Title, Link } = Typography;
const { Option } = Select;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatMontant = (val) =>
    val != null ? val.toLocaleString("fr-MG") + " Ar" : "- Ar";

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getMontantTotal = (devis_infos) => {
    if (!devis_infos) return 0;
    const infos =
        typeof devis_infos === "string" ? JSON.parse(devis_infos) : devis_infos;
    return infos.reduce((sum, d) => sum + (d.montant || 0), 0);
};

const getInitiales = (nom) =>
    nom
        ?.split(" ")
        .map((p) => p[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "?";

// ─── Config statuts ───────────────────────────────────────────────────────────
const STATUS_STYLES = {
    processing: { color: "#1677ff", bg: "#e6f4ff", border: "#91caff", icon: <SyncOutlined spin /> },
    warning: { color: "#faad14", bg: "#fffbe6", border: "#ffe58f", icon: <ClockCircleOutlined /> },
    success: { color: "#52c41a", bg: "#f6ffed", border: "#b7eb8f", icon: <CheckCircleOutlined /> },
    error: { color: "#ff4d4f", bg: "#fff2f0", border: "#ffccc7", icon: <ExclamationCircleOutlined /> },
    default: { color: "#8c8c8c", bg: "#fafafa", border: "#d9d9d9", icon: <FileTextOutlined /> },
};

const STATUT_FACTURE_CONFIG = {
    attente_masoandro: { ...STATUS_STYLES.processing, label: "En attente Masoandro" },
    valide_direction: { ...STATUS_STYLES.success, label: "Validé par la direction" },
    invalide_direction: { ...STATUS_STYLES.error, label: "Refusé par la direction" },
    attente_client: { ...STATUS_STYLES.warning, label: "En attente client" },
    valide_client: { ...STATUS_STYLES.success, label: "Validé par le client" },
    invalide_client: { ...STATUS_STYLES.error, label: "Refusé par le client" },
};

const getStatutConfig = (statut) =>
    STATUT_FACTURE_CONFIG[statut] || { ...STATUS_STYLES.default, label: statut || "-" };

// ─── StatutBadge ──────────────────────────────────────────────────────────────
const StatutBadge = ({ statut }) => {
    const cfg = getStatutConfig(statut);
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "2px 8px",
                borderRadius: 20,
                background: cfg.bg,
                border: `1px solid ${cfg.border}`,
                color: cfg.color,
                fontSize: 8,
                fontWeight: 600,
                whiteSpace: "nowrap",
            }}
        >
            {cfg.icon}
            {cfg.label}
        </span>
    );
};

// ─── Modal changement de statut ───────────────────────────────────────────────
const ModalChangerStatut = ({ facture, open, onClose, onSuccess }) => {
    const [selectedStatut, setSelectedStatut] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setSelectedStatut(facture?.statut_facture ?? null);
    }, [open, facture]);

    const handleConfirm = async () => {
        if (!selectedStatut || selectedStatut === facture?.statut_facture) {
            onClose();
            return;
        }
        setSaving(true);
        try {
            await updateStatutFacture({ facture_id: facture.facture_id, statut: selectedStatut });
            message.success("Statut mis à jour avec succès");
            onSuccess(facture.facture_id, selectedStatut);
            onClose();
        } catch (err) {
            console.error(err);
            message.error("Erreur lors de la mise à jour du statut");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            onOk={handleConfirm}
            okText="Confirmer"
            cancelText="Annuler"
            confirmLoading={saving}
            okButtonProps={{
                disabled: !selectedStatut || selectedStatut === facture?.statut_facture,
                style: { background: "#5b6af0", borderColor: "#5b6af0" },
            }}
            title={
                <Space size={10}>
                    <SwapOutlined style={{ color: "#5b6af0" }} />
                    <span style={{ fontWeight: 700 }}>Changer le statut</span>
                </Space>
            }
            width={480}
            styles={{
                header: { borderBottom: "1px solid #f0f0f0", paddingBottom: 14 },
                body: { paddingTop: 20, paddingBottom: 8 },
            }}
        >
            <div
                style={{
                    background: "#f5f6fa",
                    borderRadius: 10,
                    padding: "12px 16px",
                    marginBottom: 20,
                }}
            >
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    <Space>
                        <Text type="secondary" style={{ fontSize: 12, width: 90 }}>Référence</Text>
                        <Text strong style={{ color: "#5b6af0", fontSize: 13 }}>{facture?.facture_ref}</Text>
                    </Space>
                    <Space>
                        <Text type="secondary" style={{ fontSize: 12, width: 90 }}>Client</Text>
                        <Text strong style={{ fontSize: 13 }}>{facture?.client_nom}</Text>
                    </Space>
                    <Space>
                        <Text type="secondary" style={{ fontSize: 12, width: 90 }}>Statut actuel</Text>
                        <StatutBadge statut={facture?.statut_facture} />
                    </Space>
                </Space>
            </div>

            <Text type="secondary" style={{ fontSize: 12, marginBottom: 10, display: "block" }}>
                Sélectionner le nouveau statut
            </Text>

            <Radio.Group
                value={selectedStatut}
                onChange={(e) => setSelectedStatut(e.target.value)}
                style={{ width: "100%" }}
            >
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    {Object.entries(STATUT_FACTURE_CONFIG).map(([key, cfg]) => {
                        const isCurrentStatut = key === facture?.statut_facture;
                        const isSelected = key === selectedStatut;
                        return (
                            <Radio key={key} value={key} style={{ width: "100%" }}>
                                <div
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        width: "100%",
                                        padding: "8px 12px",
                                        borderRadius: 8,
                                        border: `1px solid ${isSelected ? cfg.border : "#f0f0f0"}`,
                                        background: isSelected ? cfg.bg : "#fff",
                                        cursor: "pointer",
                                        transition: "all .15s",
                                    }}
                                >
                                    <Space size={8}>
                                        <span style={{ color: cfg.color, fontSize: 14 }}>{cfg.icon}</span>
                                        <Text style={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? cfg.color : "#262626" }}>
                                            {cfg.label}
                                        </Text>
                                    </Space>
                                    {isCurrentStatut && (
                                        <Text style={{ fontSize: 11, color: "#8c8c8c", background: "#f0f0f0", padding: "1px 7px", borderRadius: 10 }}>
                                            actuel
                                        </Text>
                                    )}
                                </div>
                            </Radio>
                        );
                    })}
                </Space>
            </Radio.Group>
        </Modal>
    );
};

// ─── StatCard compacte (ligne horizontale) ────────────────────────────────────
const StatCard = ({ icon, label, value, color, bgColor }) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "#fff",
            borderRadius: 10,
            padding: "10px 16px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            height: "100%",
        }}
    >
        <div
            style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: bgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                color: color,
                flexShrink: 0,
            }}
        >
            {icon}
        </div>
        <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: "#8c8c8c", lineHeight: 1.2, whiteSpace: "nowrap" }}>
                {label}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1.3, whiteSpace: "nowrap" }}>
                {value}
            </div>
        </div>
    </div>
);

// ─── Composant principal ──────────────────────────────────────────────────────
const FactureTransit = () => {
    const [search, setSearch] = useState("");
    const [filterStatut, setFilterStatut] = useState("tous");
    const [filterAgence, setFilterAgence] = useState("toutes");
    const [loading, setLoading] = useState(false);
    const [listeFactures, setListeFactures] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [factureSelected, setFactureSelected] = useState(null);
    const [modalDemandeProv, setModalDemandeProv] = useState(false);
    const [allDocuments, setallDocuments] = useState([]);
    const idsExclus = [1, 2, 3, 4, 6];
    const [provNumber, setprovNumber] = useState(0);
    const openModal = (facture) => { setFactureSelected(facture); setModalOpen(true); };
    const closeModal = () => { setModalOpen(false); setFactureSelected(null); };
    const [lignesProvision, setLignesProvision] = useState([{ uid: crypto.randomUUID(), id: null, nom: null, montant: null, taxe: null, tva: null, qte: null }]);

    const [showToast, setshowToast] = useState(false);
    const [notif, setnotif] = useState({ message: null, info: null });

    const handleStatutUpdated = (factureId, newStatut) => {
        setListeFactures((prev) =>
            prev.map((f) => f.facture_id === factureId ? { ...f, statut_facture: newStatut } : f)
        );
    };

    const data = useMemo(
        () => listeFactures.map((row) => ({ ...row, montant_total: getMontantTotal(row.devis_infos) })),
        [listeFactures]
    );

    const agences = useMemo(
        () => [...new Set(data.map((d) => d.agence_nom).filter(Boolean))],
        [data]
    );

    const fetchFactures = async () => {
        setLoading(true);
        try {
            const response = await getTransitFacture();
            console.log(response.data);
            setListeFactures(response.data || []);
        } catch (err) {
            console.error("Erreur chargement factures :", err);
            setListeFactures([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFactures();
        fetchAllTypeDocument();
    }, []);

    const filtered = useMemo(() => {
        return data.filter((row) => {
            const q = search.toLowerCase();
            const matchSearch =
                !q ||
                row.facture_ref?.toLowerCase().includes(q) ||
                row.client_nom?.toLowerCase().includes(q) ||
                row.dossier_ref?.toLowerCase().includes(q) ||
                row.created_by?.toLowerCase().includes(q);
            const matchStatut = filterStatut === "tous" || row.statut_facture === filterStatut;
            const matchAgence = filterAgence === "toutes" || row.agence_nom === filterAgence;
            return matchSearch && matchStatut && matchAgence;
        });
    }, [data, search, filterStatut, filterAgence]);

    const stats = useMemo(() => {
        const total = data.length;
        const enAttente = data.filter((d) => d.statut_facture === "attente_masoandro").length;
        const validees = data.filter((d) =>
            d.statut_facture === "validee" || d.statut_facture === "valide_direction"
        ).length;
        const montantTotal = data.reduce((s, d) => s + d.montant_total, 0);
        return { total, enAttente, validees, montantTotal };
    }, [data]);


    const handleDownloadFacture = (facture) => async () => {
        await downloadFactureTransit(facture);
    };

    const handleDemandeDeProvision = (facture) => {
        setFactureSelected(facture)
        setprovNumber(facture.nb_conteneur);
        const devis_info = facture.devis_infos
            ? JSON.parse(facture.devis_infos)
            : [];
        const lignes = devis_info
            .filter((item) => {
                const document = allDocuments.find(
                    (doc) => Number(doc.id) === Number(item.categorie_id)
                );

                return document?.type === "A L'IDENTIQUE";
            })
            .map((item) => {
                const document = allDocuments.find(
                    (doc) => Number(doc.id) === Number(item.categorie_id)
                );

                return {
                    uid: crypto.randomUUID(),
                    id: item.categorie_id,
                    nom: document?.nom || null,
                    taxe: item.taxe || 0,
                    tva: item.tva || 0,
                    montant: item.montant,
                };
            });
        console.log(lignes);
        setLignesProvision(lignes);
        setFactureSelected(facture);
        setModalDemandeProv(true)
    }

    const fetchAllTypeDocument = async () => {
        const res = await getAllCategoriesTransit();
        setallDocuments(res.data);
    };

    // ─── Colonnes ─────────────────────────────────────────────────────────────
    const columns = [
        {
            title: "Référence",
            dataIndex: "facture_ref",
            key: "facture_ref",
            width: 155,
            sorter: (a, b) => a.facture_ref?.localeCompare(b.facture_ref),
            render: (ref) => (
                <Link style={{ color: "#5b6af0", fontWeight: 600, fontSize: 8, letterSpacing: 0.2 }}>
                    {ref || "-"}
                </Link>
            ),
        },
        {
            title: "Client",
            dataIndex: "client_nom",
            key: "client_nom",
            width: 120,
            sorter: (a, b) => a.client_nom?.localeCompare(b.client_nom),
            render: (nom) => <Text strong style={{ fontSize: 8 }}>{nom || "-"}</Text>,
        },
        {
            title: "Dossier",
            key: "dossier",
            width: 145,
            render: (_, row) => (
                <div>
                    <Text style={{ fontSize: 8, fontWeight: 500 }}>{row.dossier_ref}</Text>
                    <br />
                    {/* <Text type="secondary" style={{ fontSize: 11 }}>{row.dossier_statut}</Text> */}
                </div>
            ),
        },
        {
            title: "Agence",
            dataIndex: "agence_nom",
            key: "agence_nom",
            width: 115,
            sorter: (a, b) => a.agence_nom?.localeCompare(b.agence_nom),
            render: (agence) => (
                <Space size={4}>
                    <BankOutlined style={{ color: "#5b6af0", fontSize: 11 }} />
                    <Text style={{ color: "#5b6af0", fontWeight: 500, fontSize: 8 }}>{agence}</Text>
                </Space>
            ),
        },
        {
            title: "Créé par",
            dataIndex: "created_by",
            key: "created_by",
            width: 145,
            render: (created_by, row) => (
                <Space size={6}>
                    {/* <Avatar size={22} style={{ background: "#5b6af0", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                        {getInitiales(row.dossier_created_by)}
                    </Avatar> */}
                    <Text style={{ fontSize: 8 }}>{created_by}</Text>
                </Space>
            ),
        },
        {
            title: "Date",
            dataIndex: "facture_date",
            key: "facture_date",
            width: 105,
            sorter: (a, b) => new Date(a.facture_date) - new Date(b.facture_date),
            render: (date) => <Text style={{ fontSize: 8 }}>{formatDate(date)}</Text>,
        },
        {
            title: "Montant",
            dataIndex: "montant_total",
            key: "montant_total",
            width: 135,
            sorter: (a, b) => a.montant_total - b.montant_total,
            render: (montant) => (
                <Text strong style={{ color: "#262626", fontSize: 8 }}>{formatMontant(montant)}</Text>
            ),
        },
        {
            title: "Statut",
            dataIndex: "statut_facture",
            key: "statut_facture",
            width: 175,
            filters: Object.entries(STATUT_FACTURE_CONFIG).map(([k, v]) => ({ text: v.label, value: k })),
            onFilter: (value, record) => record.statut_facture === value,
            render: (statut) => <StatutBadge statut={statut} />,
        },
        {
            title: "Actions",
            key: "actions",
            width: 115,
            fixed: "right",
            render: (_, row) => (
                <Space size={2}>
                    <Tooltip title="Télécharger la facture">
                        <Button type="text" size="small" icon={<FileTextOutlined />} style={{ color: "#088920" }} onClick={handleDownloadFacture(row)} />
                    </Tooltip>
                    <Tooltip title="Demande de provision">
                        <Button shape="circle" icon={<CalculatorOutlined />} size="small"
                            onClick={
                                (e) => {
                                    e.stopPropagation();
                                }
                            }
                            style={{ border: "1px solid #d1fae5", color: "#059669", background: "#f0fdf4" }}
                            onClick={() => {
                                handleDemandeDeProvision(row);
                            }}
                        />
                    </Tooltip>
                    <Tooltip title="Changer le statut">
                        <Button type="text" size="small" icon={<SwapOutlined />} style={{ color: "#5b6af0" }} onClick={() => openModal(row)} />
                    </Tooltip>
                    <Tooltip title="Modifier">
                        <Button type="text" size="small" icon={<EditOutlined />} style={{ color: "#a6e512" }} onClick={() => console.log("modifier", row.facture_ref)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];


    const handleAskProvision = async () => {
        console.log(factureSelected);
        console.log(lignesProvision);
        const payload = {
            "facture_id": factureSelected.facture_id,
            "dossier_id": factureSelected.dossier_id,
            "lignes" : JSON.stringify(lignesProvision),
            "qte": provNumber
        }
        const res = await createProvision(payload);
        
        if (res.status === "success") {
            setnotif({ message: "Provision créée avec succès", info: "success" });
            setshowToast(true);
            setTimeout(() => { window.location.reload(); }, 1500);
        } else {
            message.warning("Erreur lors de la création de la provision");
            setnotif({ message: "Erreur lors de la création de la provision", info: "error" });
            setshowToast(true);
            setTimeout(() => { setshowToast(false); }, 1500);
        }
    }

    return (
        <div
            style={{
                minHeight: "100vh",
                background: "#f5f6fa",
                padding: "20px 32px",
                fontFamily: "'Inter', sans-serif",
                display: "flex",
                flexDirection: "column",
                gap: 14,
            }}
        >
            {/* ── Header + Stats sur la même ligne ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
                {/* Titre */}
                <div style={{ flexShrink: 0 }}>
                    <Space size={8} align="center">
                        <FileTextOutlined style={{ fontSize: 18, color: "#5b6af0" }} />
                        <Title level={4} style={{ margin: 0, fontWeight: 700 }}>
                            Factures Transit
                        </Title>
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 2 }}>
                        Gestion et suivi des factures de transit
                    </Text>
                </div>

                {/* Séparateur */}
                <div style={{ width: 1, height: 40, background: "#e8e8e8", flexShrink: 0 }} />

                {/* Stats compactes */}
                <div style={{ display: "flex", gap: 10, flex: 1, flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 140px", minWidth: 130 }}>
                        <StatCard icon={<FileTextOutlined />} label="Total factures" value={stats.total} color="#5b6af0" bgColor="#eeeffd" />
                    </div>
                    <div style={{ flex: "1 1 140px", minWidth: 130 }}>
                        <StatCard icon={<ClockCircleOutlined />} label="En attente" value={stats.enAttente} color="#fa8c16" bgColor="#fff7e6" />
                    </div>
                    <div style={{ flex: "1 1 140px", minWidth: 130 }}>
                        <StatCard icon={<CheckCircleOutlined />} label="Validées" value={stats.validees} color="#52c41a" bgColor="#f6ffed" />
                    </div>
                    <div style={{ flex: "1 1 180px", minWidth: 160 }}>
                        <StatCard icon={<DollarOutlined />} label="Montant total" value={formatMontant(stats.montantTotal)} color="#13c2c2" bgColor="#e6fffb" />
                    </div>
                </div>

                {/* Bouton actualiser */}
                <Button
                    icon={<ReloadOutlined spin={loading} />}
                    onClick={fetchFactures}
                    style={{
                        border: "none",
                        color: "#5b6af0",
                        background: "transparent",
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        flexShrink: 0,
                    }}
                >
                    ACTUALISER
                </Button>
            </div>

            {/* ── Tableau ── */}
            <Card
                bordered={false}
                style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", flex: 1 }}
                bodyStyle={{ padding: 0 }}
            >
                {/* Barre de filtres */}
                <div
                    style={{
                        padding: "12px 16px",
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        flexWrap: "wrap",
                        borderBottom: "1px solid #f0f0f0",
                    }}
                >
                    <Input
                        prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                        placeholder="Rechercher par réf, client, dossier, créateur..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ flex: 1, minWidth: 240, borderRadius: 8 }}
                        allowClear
                        size="small"
                    />
                    <Select
                        value={filterStatut}
                        onChange={setFilterStatut}
                        style={{ width: 190 }}
                        size="small"
                    >
                        <Option value="tous">Tous les statuts</Option>
                        {Object.entries(STATUT_FACTURE_CONFIG).map(([k, v]) => (
                            <Option key={k} value={k}>{v.label}</Option>
                        ))}
                    </Select>
                    <Select
                        value={filterAgence}
                        onChange={setFilterAgence}
                        style={{ width: 160 }}
                        size="small"
                    >
                        <Option value="toutes">Toutes les agences</Option>
                        {agences.map((a) => <Option key={a} value={a}>{a}</Option>)}
                    </Select>
                    <Text type="secondary" style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                        {filtered.length} résultat{filtered.length !== 1 ? "s" : ""}
                    </Text>
                </div>

                <Table
                    dataSource={filtered}
                    columns={columns}
                    rowKey="facture_id"
                    loading={loading}
                    pagination={{
                        pageSize: 12,
                        position: ["bottomCenter"],
                        style: { marginTop: 8, marginBottom: 8 },
                        size: "small",
                        itemRender: (pg, type, originalElement) => {
                            if (type === "page") {
                                return (
                                    <div style={{
                                        borderRadius: "50%", minWidth: 28, height: 28,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        border: "1px solid #d9d9d9", fontSize: 12,
                                    }}>
                                        {pg}
                                    </div>
                                );
                            }
                            return originalElement;
                        },
                    }}
                    scroll={{ x: "max-content" }}
                    size="small"
                    rowClassName={(_, idx) => (idx % 2 === 0 ? "row-even" : "row-odd")}
                    style={{ borderTop: "none" }}
                />
            </Card>

            {/* ── Modal statut ── */}
            <ModalChangerStatut
                facture={factureSelected}
                open={modalOpen}
                onClose={closeModal}
                onSuccess={handleStatutUpdated}
            />

            <Modal
                open={modalDemandeProv}
                onCancel={() => {
                    setLignesProvision([{ id: Date.now(), nom: null, montant: null }]);
                    setprovNumber(0);
                    setModalDemandeProv(false);

                }}
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <FileTextOutlined style={{ color: '#1677ff', fontSize: 16 }} />
                        <span style={{ fontSize: 15, fontWeight: 700 }}>Demande de provision</span>
                    </div>
                }
                width="90%"
                footer={null}
                style={{ top: 15 }}
                styles={{ body: { padding: '20px 24px' } }}
            >
                {/* Header tableau */}
                <Row
                    style={{
                    padding: '0 0 8px',
                    borderBottom: '2px solid #1677ff',
                }}
                >
                    <Col span={4} style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rubrique</span>
                    </Col>
                    <Col span={1}></Col>
                    <Col span={1} style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Qte</span>
                    </Col>
                    <Col span={1}></Col>
                    <Col span={2} style={{ textAlign: 'center' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>PU</span>
                    </Col>
                    <Col span={1}></Col>
                    <Col span={3} style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Taxe</span>
                    </Col>
                    <Col span={1}></Col>
                    <Col span={3} style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>TVA</span>
                    </Col>
                    <Col span={1}></Col>
                    <Col span={3} style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total</span>
                    </Col>
                    <Col span={2}>

                    </Col>
                    <Col span={1}></Col>
                </Row>
                <br/>
                {/* <div style={{
                    display: 'grid', gridTemplateColumns: '40px 1fr 180px 36px',
                    gap: 12, padding: '0 0 8px',
                    borderBottom: '2px solid #1677ff',
                    marginBottom: 6
                }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>#</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Rubrique</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>Montant (Ar)</span>
                    <span />
                </div> */}

                {/* Lignes dynamiques */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 380, overflowY: 'auto', paddingRight: 2 }}>
                    {lignesProvision.map((ligne) => (
                        <Row>
                            <Col span={4}>
                                <Select
                                    placeholder="Choisir une rubrique"
                                    value={ligne.nom || undefined}
                                    onChange={(value) => {
                                        const docSelectionne = allDocuments.find(d => d.nom === value);
                                        setLignesProvision(prev =>
                                            prev.map(l => l.uid === ligne.uid ? {
                                                ...l,
                                                id: docSelectionne?.id,
                                                nom: value
                                            } : l)
                                        )
                                    }}
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    // options={allDocuments.map(d => ({ value: d.nom, label: d.nom }))}
                                    options={allDocuments
                                        .filter(doc => doc.type === "A L'IDENTIQUE")
                                        .map(doc => ({
                                            value: doc.nom,
                                            label: doc.nom
                                        }))
                                    }
                                />
                            </Col>
                            <Col span={1}></Col>
                            <Col span={1}>
                                <InputNumber
                                    style={{ width: '100%', textAlign: 'center' }}
                                    size="small"
                                    placeholder="0"
                                    value={provNumber}
                                    disabled
                                />
                            </Col>
                            <Col span={1}></Col>
                            <Col span={2}>
                                <InputNumber
                                    size="small"
                                    placeholder="0"
                                    min={0}
                                    value={ligne.montant}
                                    onChange={(value) =>
                                        setLignesProvision(prev =>
                                            prev.map(l => l.id === ligne.id ? { ...l, montant: value } : l)
                                        )
                                    }
                                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                    parser={v => v.replace(/\s?/g, '')}
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    styles={{ input: { textAlign: 'right' } }}
                                />
                            </Col>
                            <Col span={1}></Col>
                            <Col span={3}>
                                <InputNumber
                                    size="small"
                                    placeholder="Taxes"
                                    min={0}
                                    style={{ width: '100%' }}
                                    value={ligne.taxe}
                                    onChange={(value) =>
                                        setLignesProvision(prev =>
                                            prev.map(l => l.id === ligne.id ? { ...l, taxe: value } : l)
                                        )
                                    }
                                />
                            </Col>
                            <Col span={1}></Col>
                            <Col span={3}>
                                <InputNumber
                                    size="small"
                                    placeholder="TVA"
                                    min={0}
                                    style={{ width: '100%' }}
                                    value={ligne.tva}
                                    onChange={(value) =>
                                        setLignesProvision(prev =>
                                            prev.map(l => l.id === ligne.id ? { ...l, tva: value } : l)
                                        )
                                    }
                                />
                            </Col>
                            <Col span={1}></Col>
                            <Col span={3}>
                                <InputNumber
                                    size="small"
                                    placeholder="0"
                                    min={0}
                                    value={(ligne.montant * provNumber) + ligne.taxe + ((ligne.montant * provNumber) * ligne.tva / 100)}
                                    onChange={(value) =>
                                        setLignesProvision(prev =>
                                            prev.map(l => l.id === ligne.id ? { ...l, montant: value } : l)
                                        )
                                    }
                                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                                    parser={v => v.replace(/\s?/g, '')}
                                    style={{ width: '100%' }}
                                    variant="filled"
                                    styles={{ input: { textAlign: 'right' } }}
                                />
                            </Col>
                            <Col span={2}>
                                <Button
                                    type="text"
                                    danger
                                    icon={<MinusCircleOutlined />}
                                    size="small"
                                    disabled={lignesProvision.length === 1}
                                    onClick={() =>
                                        setLignesProvision(prev => prev.filter(l => l.id !== ligne.id))
                                    }
                                    style={{ color: lignesProvision.length === 1 ? '#ccc' : '#ff4d4f' }}
                                />
                            </Col>
                            <Col span={1}></Col>
                        </Row>
                    ))}
                </div>

                <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() =>
                        setLignesProvision(prev => [...prev, { uid: crypto.randomUUID(), id: null, nom: null, montant: null }])
                    }
                    style={{ width: '100%', marginTop: 12, borderRadius: 6, color: '#1677ff', borderColor: '#1677ff' }}
                >
                    Ajouter une rubrique
                </Button>

                {/* Total + Actions */}
                <div style={{
                    marginTop: 16, paddingTop: 14,
                    borderTop: '1px solid #f0f0f0',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                    <div>
                        <span style={{ fontSize: 12, color: '#888' }}>Total : </span>
                        <span style={{ fontSize: 16, fontWeight: 700, color: '#1677ff' }}>
                            {lignesProvision
                                .reduce((s, l) => s + (parseFloat((l.montant * provNumber) + l.taxe + ((l.montant * provNumber) * l.tva / 100)) || 0), 0)
                                .toLocaleString('fr-FR', { minimumFractionDigits: 2 })} Ar
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            onClick={() => {
                                setLignesProvision([{ id: Date.now(), nom: null, montant: null, taxe: null, tva: null }]);
                                setModalDemandeProv(false);
                            }}
                            style={{ borderRadius: 6 }}
                        >
                            Annuler
                        </Button>
                        <Button
                            type="primary"
                            style={{ borderRadius: 6, fontWeight: 600, minWidth: 120 }}
                            onClick={() => {
                                handleAskProvision()
                            }}
                        >
                            Confirmer
                        </Button>
                    </div>
                </div>
            </Modal>

            {showToast && <Notification message={notif.message} status={notif.info} />}

            <style>{`
                .row-even { background: #fff; }
                .row-odd  { background: #fafbff; }
                .ant-table-tbody > tr:hover > td { background: #f0f2ff !important; }
                .ant-table-thead > tr > th {
                    background: #fafafa !important;
                    font-weight: 600;
                    color: #595959;
                    font-size: 12px;
                    border-bottom: 1px solid #f0f0f0;
                    padding: 8px 12px !important;
                }
                .ant-table-tbody > tr > td {
                    padding: 8px 12px !important;
                }
                .ant-radio-wrapper { width: 100%; }
                .ant-radio-wrapper > span:last-child { width: 100%; padding-right: 0; }
            `}</style>
        </div>
    );
};

export default FactureTransit;